const MONTHS_FR = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Aoû", "Sep", "Oct", "Nov", "Déc"];

const finite = (value) => Number.isFinite(Number(value));
const mean = (values) => values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
const fr = (value, digits = 1) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: digits }).format(value);

export const monthLabel = (period) => {
  const month = Number(String(period || "").slice(5, 7));
  return MONTHS_FR[month - 1] || period;
};

export function buildTourismDashboardView(payload) {
  const tourism = (payload?.tourism || [])
    .filter((row) => /^\d{4}-\d{2}$/.test(row.period || ""))
    .slice()
    .sort((a, b) => a.period.localeCompare(b.period));
  const occupancy = (payload?.occupancy || [])
    .filter((row) => /^\d{4}-\d{2}$/.test(row.period || ""))
    .slice()
    .sort((a, b) => a.period.localeCompare(b.period));
  const arrivals = tourism.filter((row) => finite(row.visitor_arrivals)).slice(-12);
  const occupancy12 = occupancy.filter((row) => finite(row.room_occupancy_rate) || finite(row.bed_occupancy_rate)).slice(-12);
  const totalArrivals = arrivals.reduce((sum, row) => sum + Number(row.visitor_arrivals), 0);
  // The reporting contract only permits EUR or USD. This frontend guard keeps
  // an older/misaligned API from leaking local currencies into decision views.
  const receiptsRows = arrivals.filter((row) =>
    finite(row.tourism_receipts) && ["EUR", "USD"].includes(String(row.tourism_receipts_currency || "").toUpperCase()));
  const totalReceipts = receiptsRows.reduce((sum, row) => sum + Number(row.tourism_receipts), 0);
  const receiptCurrencies = [...new Set(receiptsRows.map((row) => String(row.tourism_receipts_currency).toUpperCase()))];
  const stayRows = arrivals.filter((row) => finite(row.average_stay));
  const weightedStayDenominator = stayRows.reduce((sum, row) => sum + (finite(row.visitor_arrivals) ? Number(row.visitor_arrivals) : 0), 0);
  const averageStay = weightedStayDenominator
    ? stayRows.reduce((sum, row) => sum + Number(row.average_stay) * Number(row.visitor_arrivals), 0) / weightedStayDenominator
    : mean(stayRows.map((row) => Number(row.average_stay)));
  const avgRoom = mean(occupancy12.filter((row) => finite(row.room_occupancy_rate)).map((row) => Number(row.room_occupancy_rate)));
  const avgBed = mean(occupancy12.filter((row) => finite(row.bed_occupancy_rate)).map((row) => Number(row.bed_occupancy_rate)));
  const arrivalsMean = arrivals.length ? totalArrivals / arrivals.length : null;

  return {
    hasTourism: arrivals.length > 0,
    hasOccupancy: occupancy12.length > 0,
    period: arrivals.length ? `${arrivals[0].period} → ${arrivals[arrivals.length - 1].period}` : null,
    occupancyPeriod: occupancy12.length ? `${occupancy12[0].period} → ${occupancy12[occupancy12.length - 1].period}` : null,
    evolution: arrivals.map((row) => ({
      period: row.period,
      month: monthLabel(row.period),
      visitors: Math.round(Number(row.visitor_arrivals)),
    })),
    seasonality: arrivalsMean ? arrivals.map((row) => ({
      period: row.period,
      month: monthLabel(row.period),
      value: Math.round(Number(row.visitor_arrivals) / arrivalsMean * 100),
      raw: Math.round(Number(row.visitor_arrivals)),
    })) : [],
    occupancy: occupancy12.map((row) => ({
      period: row.period,
      month: monthLabel(row.period),
      rooms: finite(row.room_occupancy_rate) ? Number(row.room_occupancy_rate) : null,
      beds: finite(row.bed_occupancy_rate) ? Number(row.bed_occupancy_rate) : null,
    })),
    kpis: {
      arrivals: totalArrivals || null,
      averageStay,
      receipts: receiptCurrencies.length === 1 && totalReceipts ? totalReceipts : null,
      receiptsCurrency: receiptCurrencies.length === 1 ? receiptCurrencies[0] : null,
      receiptsFx: receiptsRows.find((row) => row.tourism_receipts_conversion_status === "converted") ? {
        sourceCurrency: receiptsRows.find((row) => row.tourism_receipts_source_currency)?.tourism_receipts_source_currency || null,
        asOf: receiptsRows.find((row) => row.tourism_receipts_fx_as_of)?.tourism_receipts_fx_as_of || null,
        source: receiptsRows.find((row) => row.tourism_receipts_fx_source)?.tourism_receipts_fx_source || null,
      } : null,
      roomOccupancy: avgRoom,
      bedOccupancy: avgBed,
    },
    provisional: [...arrivals, ...occupancy12].some((row) =>
      Object.entries(row).some(([key, value]) => key.endsWith("_quality") && String(value).toUpperCase() === "PROVISIONAL")),
  };
}

export function buildCrossDataInsights(view, markets, air, options = {}) {
  const insights = [];
  const selectedMarket = options.selectedMarket || null;
  const selectedMarketLabel = options.selectedMarketLabel || selectedMarket?.name || "Marché sélectionné";
  const arrivals = view?.evolution || [];
  const occupancy = view?.occupancy || [];
  if (arrivals.length) {
    const peak = arrivals.reduce((best, row) => row.visitors > best.visitors ? row : best);
    const trough = arrivals.reduce((best, row) => row.visitors < best.visitors ? row : best);
    insights.push({
      id: "seasonality",
      title: "Saisonnalité observée",
      scope: "Destination globale",
      text: `${peak.month} atteint ${fr(peak.visitors, 0)} arrivées, contre ${fr(trough.visitors, 0)} en ${trough.month}.`,
      implication: `Concentrer l'acquisition avant ${trough.month} pour soutenir la période la plus faible.`,
    });
  }
  if (occupancy.length) {
    const roomRows = occupancy.filter((row) => finite(row.rooms));
    if (roomRows.length) {
      const low = roomRows.reduce((best, row) => row.rooms < best.rooms ? row : best);
      const arrivalAtLow = arrivals.find((row) => row.period === low.period);
      const arrivalAverage = mean(arrivals.map((row) => row.visitors));
      const demandContext = arrivalAtLow && arrivalAverage
        ? ` Les arrivées du même mois (${fr(arrivalAtLow.visitors, 0)}) sont ${arrivalAtLow.visitors < arrivalAverage ? "sous" : "au-dessus de"} la moyenne mensuelle.`
        : "";
      insights.push({
        id: "occupancy",
        title: "Arrivées × occupation",
        scope: "Destination globale",
        text: `Le taux d'occupation chambres moyen est de ${fr(view.kpis.roomOccupancy)} %, avec un point bas à ${fr(low.rooms)} % en ${low.month}.${demandContext}`,
        implication: `Les actions de stimulation doivent prioriser ${low.month}, sous réserve de la capacité commercialisable réelle.`,
      });
    }
  }
  if (view?.kpis?.receipts && view?.kpis?.arrivals) {
    const spend = view.kpis.receipts / view.kpis.arrivals;
    insights.push({
      id: "value",
      title: "Valeur touristique",
      scope: "Destination globale",
      text: `Les recettes représentent environ ${fr(spend, 0)} ${view.kpis.receiptsCurrency || ""} par arrivée sur la période commune.`,
      implication: "Suivre ce rendement avec le volume pour arbitrer acquisition de masse et segments à forte valeur.",
    });
  }
  if (selectedMarket && finite(selectedMarket.volume)) {
    const growth = finite(selectedMarket.evolution)
      ? `, avec une évolution de ${Number(selectedMarket.evolution) > 0 ? "+" : ""}${fr(selectedMarket.evolution)} %`
      : "";
    const share = finite(selectedMarket.part_destination)
      ? ` et ${fr(selectedMarket.part_destination)} % des arrivées comparables de la destination`
      : "";
    insights.unshift({
      id: "selected-market",
      title: `Performance — ${selectedMarketLabel}`,
      scope: "Marché sélectionné",
      text: `${selectedMarketLabel} représente ${fr(selectedMarket.volume, 0)} arrivées${share}${growth}.`,
      implication: "Dimensionner la campagne sur la dynamique propre de ce marché, sans confondre ses résultats avec les totaux de la destination.",
    });
  }
  const topMarket = selectedMarket || (markets || []).find((row) => finite(row.part_destination));
  const topAirline = air?.topAirlinesByPassengers?.[0];
  if (selectedMarket && topAirline) {
    insights.push({
      id: "air-demand",
      title: "Aérien × marché émetteur",
      scope: "Marché sélectionné × destination",
      text: `${selectedMarketLabel} représente ${finite(topMarket.part_destination) ? `${fr(topMarket.part_destination)} %` : "une part non disponible"} des arrivées comparables ; ${topAirline.name} porte ${fr(topAirline.share)} % des passagers sur la liaison sélectionnée.`,
      implication: "Tester une activation conjointe destination–compagnie, puis mesurer séparément volume et valeur générés.",
    });
  } else if (topMarket) {
    insights.push({
      id: "destination-top-market",
      title: "Marché principal de la destination",
      scope: "Destination globale",
      text: `${topMarket.name || "Le marché principal"} représente ${finite(topMarket.part_destination) ? `${fr(topMarket.part_destination)} %` : "une part non disponible"} des arrivées comparables de la destination.`,
      implication: "Conserver ce marché comme référence de volume, puis comparer séparément la dynamique de chaque marché sélectionné.",
    });
  }
  return insights;
}

export const formatTourismKpis = (view) => ({
  arrivals: view.kpis.arrivals == null ? "—" : new Intl.NumberFormat("fr-FR", { notation: "compact", maximumFractionDigits: 1 }).format(view.kpis.arrivals),
  averageStay: view.kpis.averageStay == null ? "—" : fr(view.kpis.averageStay),
  receipts: view.kpis.receipts == null ? "—" : new Intl.NumberFormat("fr-FR", { notation: "compact", maximumFractionDigits: 1 }).format(view.kpis.receipts),
  roomOccupancy: view.kpis.roomOccupancy == null ? "—" : fr(view.kpis.roomOccupancy),
});
